import type { ComponentType, SVGProps } from 'react';

import {
  AlertCircle,
  ArrowLeft,
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Coins,
  Copy,
  Diamond,
  Download,
  ExternalLink,
  FilePlus,
  Folder,
  Gem,
  Glasses,
  HardDrive,
  Image as LImage,
  ImageIcon,
  Lock,
  Mountain,
  Palette,
  Plus,
  Share2,
  Shirt,
  ShoppingBag,
  Smile,
  Sparkles,
  Star,
  User,
  Wand2,
  Brush,
  X,
  type LucideProps,
} from 'lucide-react';

export interface IconProps {
  width?: number | string;
  height?: number | string;
  fill?: string;
  className?: string;
  strokeWidth?: number;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<SVGSVGElement>;
}

type LucideIcon = ComponentType<LucideProps>;

const makeIcon = (Lucide: LucideIcon, opts?: { filled?: boolean }) => {
  const Wrapped = ({ width = 24, height = 24, fill = 'currentColor', className, strokeWidth, style, onClick }: IconProps) => (
    <Lucide
      size={typeof width === 'number' ? width : undefined}
      width={width}
      height={height}
      color={fill}
      fill={opts?.filled ? fill : 'none'}
      strokeWidth={strokeWidth}
      className={className}
      style={style}
      onClick={onClick}
    />
  );
  Wrapped.displayName = `Icon(${Lucide.displayName ?? 'Lucide'})`;
  return Wrapped;
};

type SvgChildProps = IconProps & { children: React.ReactNode; viewBox?: string };
const SvgIcon = ({ width = 24, height = 24, fill = 'currentColor', className, viewBox = '0 0 24 24', children, style, onClick, ...rest }: SvgChildProps & SVGProps<SVGSVGElement>) => (
  <svg
    width={width}
    height={height}
    viewBox={viewBox}
    fill="none"
    stroke={fill}
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={style}
    onClick={onClick}
    {...rest}
  >
    {children}
  </svg>
);

/* ── Standard icons (lucide) ────────────────────────────────────── */
export const IconAI = makeIcon(Sparkles);
export const IconGenAI = makeIcon(Wand2);
export const IconAddPage = makeIcon(FilePlus);
export const IconArrowLeft = makeIcon(ArrowLeft);
export const IconCamera = makeIcon(Camera);
export const IconChevronDown = makeIcon(ChevronDown);
export const IconChevronLeft = makeIcon(ChevronLeft);
export const IconChevronRight = makeIcon(ChevronRight);
export const IconChevronUp = makeIcon(ChevronUp);
export const IconClock = makeIcon(Clock);
export const IconCoins = makeIcon(Coins);
export const IconCopy = makeIcon(Copy);
export const IconCross = makeIcon(X);
export const IconDiamond = makeIcon(Diamond);
export const IconDownload = makeIcon(Download);
export const IconExternalLink = makeIcon(ExternalLink);
export const IconDrive = makeIcon(HardDrive);
export const IconFolderOutline = makeIcon(Folder);
export const IconImageOutline = makeIcon(ImageIcon);
export const IconImageFilled = makeIcon(LImage, { filled: true });
export const IconBackground = makeIcon(LImage);
export const IconBody = makeIcon(User);
export const IconFace = makeIcon(Smile);
export const IconMakeup = makeIcon(Brush);
export const IconLandscape = makeIcon(Mountain);
export const IconLockFilled = makeIcon(Lock, { filled: true });
export const IconPalettes = makeIcon(Palette);
export const IconPlus = makeIcon(Plus);
export const IconShareOutline = makeIcon(Share2);
export const IconStarFilled = makeIcon(Star, { filled: true });
export const IconStarOutline = makeIcon(Star);
export const IconTick = makeIcon(Check);
export const IconWarningCircleOutline = makeIcon(AlertCircle);
export const IconGemDiamond = makeIcon(Gem);

/* ── Clothing-adjacent icons with passable lucide substitutes ──── */
export const IconSunglasses = makeIcon(Glasses);
export const IconTShirt = makeIcon(Shirt);
export const IconToteBag = makeIcon(ShoppingBag);

/* ── Clothing icons with no lucide equivalent — hand-sourced ───── */
export const IconJacket = (props: IconProps) => (
  <SvgIcon {...props}>
    <path d="M8 4 L4 6 L4 20 L8 20 M16 4 L20 6 L20 20 L16 20 M8 4 L12 7 L16 4 M8 4 L8 20 M16 4 L16 20 M12 7 L12 20" />
  </SvgIcon>
);

export const IconTrousers = (props: IconProps) => (
  <SvgIcon {...props}>
    <path d="M6 3 L18 3 L17 21 L13 21 L12 10 L11 21 L7 21 Z" />
  </SvgIcon>
);

export const IconSuit = (props: IconProps) => (
  <SvgIcon {...props}>
    <path d="M5 5 L9 3 L12 6 L15 3 L19 5 L19 21 L5 21 Z M9 3 L12 12 L15 3 M12 12 L12 21" />
  </SvgIcon>
);

export const IconRainBoots = (props: IconProps) => (
  <SvgIcon {...props}>
    <path d="M8 3 L8 16 C8 18 9 20 12 20 C15 20 16 18 16 16 L16 12 L20 12 L20 20 L4 20 L4 3 Z" />
  </SvgIcon>
);

export const IconStrawHat = (props: IconProps) => (
  <SvgIcon {...props}>
    <ellipse cx="12" cy="14" rx="10" ry="3" />
    <path d="M7 14 C7 9 9 5 12 5 C15 5 17 9 17 14" />
    <path d="M9 12 L15 12" />
  </SvgIcon>
);

export const IconHairStyle = (props: IconProps) => (
  <SvgIcon {...props}>
    <circle cx="12" cy="13" r="4" />
    <path d="M5 13 C5 7 8 4 12 4 C16 4 19 7 19 13" />
    <path d="M5 13 C5 16 6 18 8 19 M19 13 C19 16 18 18 16 19" />
  </SvgIcon>
);
